import React, { useMemo } from 'react'
import { Row, Col } from 'reactstrap'

import ChartCard from '../charts/ChartCard'
import DataTable from '../charts/DataTable'
import HorizontalStackedBar from '../charts/HorizontalStackedBar'
import StatTile, { StatTileRow } from '../charts/StatTile'
import TrendLine from '../charts/TrendLine'
import {
    DOCUMENT_SERIES,
    INCIDENT_BRANCH_SERIES,
    INCIDENT_TREND_SERIES,
    INCIDENT_WORKLOAD_SERIES,
    buildActionMix,
    buildActionTrend,
    buildIncidentKpis,
    buildMixTableRows,
} from '../activityReport'

/**
 * Incidents tab.
 *
 * Incidents and documents are kept strictly apart. They are different units --
 * an incident is a case, a document is a file attached to one -- so they are
 * never stacked in a bar or run as two lines on one axis, which would imply a
 * comparison that does not exist. Documents get their own chart at the bottom.
 *
 * Within the incident charts, colour separates opened from edited: two measures
 * of the same object, and the only comparison on this tab that means anything.
 */
const IncidentsTab = ({ rows, activeView, startDate, endDate }) => {
    const { kpis, trendData, byUser, byBranch, documentsByUser } = useMemo(
        () => ({
            kpis: buildIncidentKpis(rows, startDate, endDate),
            trendData: buildActionTrend(rows, startDate, endDate, INCIDENT_TREND_SERIES.map((item) => item.key)),
            byUser: buildActionMix(rows, {
                groupKeyFn: (row) => row.userName,
                actions: INCIDENT_WORKLOAD_SERIES.map((item) => item.key),
                groupKey: 'userName',
            }),
            byBranch: buildActionMix(rows, {
                groupKeyFn: (row) => row.branchName,
                actions: INCIDENT_BRANCH_SERIES.map((item) => item.key),
                groupKey: 'branchName',
            }),
            documentsByUser: buildActionMix(rows, {
                groupKeyFn: (row) => row.userName,
                actions: DOCUMENT_SERIES.map((item) => item.key),
                groupKey: 'userName',
            }),
        }),
        [rows, startDate, endDate]
    )

    const isTable = activeView === 'table'
    const hasIncidents = byUser.length > 0

    // Two bars per person from two series, so the legend names them once and
    // position carries which bar is which.
    const workloadLegend = INCIDENT_WORKLOAD_SERIES.map((item) => ({
        value: item.label,
        type: 'square',
        color: item.color,
        id: item.key,
    }))

    const trendKey = INCIDENT_TREND_SERIES[0].key
    const trendTableRows = trendData
        .filter((record) => record[trendKey])
        .map((record) => ({
            name: record.label,
            total: record[trendKey],
            values: { [trendKey]: record[trendKey] },
        }))

    return (
        <>
            <StatTileRow>
                <StatTile
                    label="Incidents created"
                    value={kpis.created.total}
                    caption="New incidents in range"
                    spark={kpis.created.spark}
                    tone="created"
                    accent="created"
                />
                <StatTile
                    label="Incident updates"
                    value={kpis.updated.total}
                    caption="Edit events, several per incident"
                    spark={kpis.updated.spark}
                    tone="updated"
                    accent="updated"
                />
                <StatTile
                    label="Documents uploaded"
                    value={kpis.documents.total}
                    caption="Distinct documents"
                    spark={kpis.documents.spark}
                    tone="documents"
                    accent="documents"
                />
                <StatTile
                    label="Most active user"
                    value={kpis.topUser.name}
                    caption={`${kpis.topUser.count.toLocaleString()} incident actions`}
                />
            </StatTileRow>

            <Row>
                <Col xs="12">
                    <ChartCard
                        title="Incidents Opened Over Time"
                        subtitle="New incidents per day"
                        isEmpty={!hasIncidents}
                    >
                        {isTable ? (
                            <DataTable rows={trendTableRows} series={INCIDENT_TREND_SERIES} rowHeader="Day" />
                        ) : (
                            // One series, so no legend box -- the title names what is plotted.
                            <TrendLine data={trendData} series={INCIDENT_TREND_SERIES} height={300} showLegend={false} />
                        )}
                    </ChartCard>
                </Col>
            </Row>

            <Row>
                <Col xs="12" xl={isTable ? 12 : 7}>
                    <ChartCard
                        title="Incident Workload by User"
                        subtitle="Opened and edited per person — creations count incidents, updates count edit events"
                        isEmpty={!hasIncidents}
                    >
                        {isTable ? (
                            <DataTable
                                rows={buildMixTableRows(byUser, INCIDENT_WORKLOAD_SERIES, 'userName')}
                                series={INCIDENT_WORKLOAD_SERIES}
                            />
                        ) : (
                            <HorizontalStackedBar
                                data={byUser}
                                series={INCIDENT_WORKLOAD_SERIES}
                                yKey="userName"
                                legendPayload={workloadLegend}
                                showTotals={false}
                                unit="users"
                            />
                        )}
                    </ChartCard>
                </Col>
                <Col xs="12" xl={isTable ? 12 : 5}>
                    <ChartCard
                        title="Where Incidents Happen"
                        subtitle="Incidents opened per branch"
                        isEmpty={!byBranch.length}
                    >
                        {isTable ? (
                            <DataTable
                                rows={buildMixTableRows(byBranch, INCIDENT_BRANCH_SERIES, 'branchName')}
                                series={INCIDENT_BRANCH_SERIES}
                                rowHeader="Branch"
                            />
                        ) : (
                            <HorizontalStackedBar
                                data={byBranch}
                                series={INCIDENT_BRANCH_SERIES}
                                yKey="branchName"
                                showLegend={false}
                                unit="branches"
                            />
                        )}
                    </ChartCard>
                </Col>
            </Row>

            <Row>
                <Col xs="12">
                    <ChartCard
                        title="Documents Uploaded by User"
                        subtitle="Counted on its own — a document is a file attached to an incident, not a comparable unit"
                        isEmpty={!documentsByUser.length}
                    >
                        {isTable ? (
                            <DataTable
                                rows={buildMixTableRows(documentsByUser, DOCUMENT_SERIES, 'userName')}
                                series={DOCUMENT_SERIES}
                            />
                        ) : (
                            <HorizontalStackedBar
                                data={documentsByUser}
                                series={DOCUMENT_SERIES}
                                yKey="userName"
                                showLegend={false}
                                unit="users"
                            />
                        )}
                    </ChartCard>
                </Col>
            </Row>
        </>
    )
}

export default IncidentsTab
